import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { api, BASE_URL } from '../utils/api';
import { getSocket } from '../utils/socket';
import ScoreBoard from '../components/common/ScoreBoard';
import type {
  Game,
  QuestionPublic,
  GroupScore,
  Group,
  ReceivedAnswer,
  RevealResult,
  IntruVoteReceived,
  IntruRevealPayload,
  IntruDetailPayload,
  Serie,
} from '../types';

interface GameStatus {
  game: Game;
  currentQuestion: QuestionPublic | null;
  groups: Group[];
  totalQuestions: number;
  currentSerie: { id: number; name: string } | null;
}

export default function AdminDashboard() {
  useAuth();
  const [status, setStatus] = useState<GameStatus | null>(null);
  const [scores, setScores] = useState<GroupScore[]>([]);
  const [answers, setAnswers] = useState<ReceivedAnswer[]>([]);
  const [buzzer, setBuzzer] = useState<{ taken: boolean; takenBy: string | null; takenByColor: string | null }>({ taken: false, takenBy: null, takenByColor: null });
  const [timeLeftMs, setTimeLeftMs] = useState(0);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [confirmEndGame, setConfirmEndGame] = useState(false);
  const [revealData, setRevealData] = useState<RevealResult | null>(null);
  const [allAnswered, setAllAnswered] = useState(false);
  const [questionEnded, setQuestionEnded] = useState(false);
  const [correctAnswerHint, setCorrectAnswerHint] = useState<string | null>(null);

  // Intru vote state (auto-started, details come via admin:intru:detail)
  const [intruVotes, setIntruVotes] = useState<IntruVoteReceived[]>([]);
  const [allVoted, setAllVoted] = useState(false);
  const [intruReveal, setIntruReveal] = useState<IntruRevealPayload | null>(null);
  const [activeIntruSerieId, setActiveIntruSerieId] = useState<number | null>(null);
  const [activeIntruMusics, setActiveIntruMusics] = useState<ReturnType<typeof Array<any>>>([]);
  const [intruDetail, setIntruDetail] = useState<IntruDetailPayload | null>(null);

  // Series management
  const [series, setSeries] = useState<Serie[]>([]);
  const [selectedSerieId, setSelectedSerieId] = useState<string>('');
  const [serieEnded, setSerieEnded] = useState(false);

  const pendingPlayRef = useRef<QuestionPublic | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const stopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const startTimer = useCallback((ms: number) => {
    clearTimer();
    setTimeLeftMs(ms);
    if (ms <= 0) return;
    const end = Date.now() + ms;
    timerRef.current = setInterval(() => {
      const rem = end - Date.now();
      if (rem <= 0) { setTimeLeftMs(0); clearTimer(); }
      else setTimeLeftMs(rem);
    }, 200);
  }, []);

  const playAudio = useCallback((q: QuestionPublic) => {
    const audio = audioRef.current;
    if (!audio || !q.audioFile) return;
    if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    audio.src = `${BASE_URL}/api/musics/stream/${q.audioFile}`;
    audio.currentTime = q.startMs / 1000;
    audio.play().catch(() => {});

    if (q.endMs > q.startMs) {
      stopTimeoutRef.current = setTimeout(() => audio.pause(), q.endMs - q.startMs);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const s = await api.get<GameStatus>('/api/game/status');
      setStatus(s);
      const { groups: gs } = await api.get<{ groups: GroupScore[] }>('/api/scores');
      setScores(gs);
    } catch { /* ignore */ }
  }, []);

  const loadSeries = useCallback(async () => {
    try {
      const data = await api.get<Serie[]>('/api/series');
      setSeries(data);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadStatus();
    loadSeries();
    const socket = getSocket();

    socket.on('game:started', () => {
      loadStatus();
      setAnswers([]);
      setRevealData(null);
      setAllAnswered(false);
      setQuestionEnded(false);
      setIntruVotes([]);
      setAllVoted(false);
      setIntruReveal(null);
      setActiveIntruSerieId(null);
      setActiveIntruMusics([]);
      setIntruDetail(null);
      setSerieEnded(false);
      setSelectedSerieId('');
    });

    socket.on('game:ended', ({ scores: s }: any) => {
      setScores(s);
      loadStatus();
      clearTimer();
      setSerieEnded(false);
    });

    socket.on('game:serie_started', () => {
      setAnswers([]);
      setRevealData(null);
      setAllAnswered(false);
      setQuestionEnded(false);
      setIntruReveal(null);
      setIntruVotes([]);
      setAllVoted(false);
      setIntruDetail(null);
      setSerieEnded(false);
      loadStatus();
    });

    socket.on('game:serie_ended', () => {
      setSerieEnded(true);
      setActiveIntruSerieId(null);
      setActiveIntruMusics([]);
      setSelectedSerieId('');
      loadStatus();
    });

    socket.on('question:start', ({ question, timeMs }: { question: QuestionPublic; timeMs: number }) => {
      setAnswers([]);
      setBuzzer({ taken: false, takenBy: null, takenByColor: null });
      setRevealData(null);
      setAllAnswered(false);
      setQuestionEnded(false);
      setIntruReveal(null);
      setCorrectAnswerHint(null);
      if (timeMs > 0) startTimer(timeMs);
      else { clearTimer(); setTimeLeftMs(0); }
      setStatus((s) => s ? { ...s, currentQuestion: question } : s);
      pendingPlayRef.current = question;
    });

    socket.on('admin:intru:detail', (data: IntruDetailPayload) => {
      setIntruDetail(data);
    });

    socket.on('admin:question:detail', ({ correctAnswer }: { questionId: number; correctAnswer: string | null }) => {
      setCorrectAnswerHint(correctAnswer);
    });

    socket.on('question:end', () => {
      clearTimer();
      setTimeLeftMs(0);
      setQuestionEnded(true);
    });

    socket.on('question:reveal', (data: RevealResult) => {
      setRevealData(data);
      setScores(data.scores);
    });

    socket.on('scores:update', ({ scores: s }: any) => setScores(s));

    socket.on('answer:received', (a: ReceivedAnswer) => {
      setAnswers((prev) => [...prev.filter((x) => x.groupId !== a.groupId), a]);
      if (a.suggestedCorrect !== null && a.suggestedCorrect !== undefined) {
        api.post(`/api/game/question/${a.questionId}/mark`, { groupId: a.groupId, correct: a.suggestedCorrect })
          .then(() => {
            setAnswers((prev) => prev.map((x) =>
              x.groupId === a.groupId ? { ...x, marked: true, correct: a.suggestedCorrect } : x
            ));
          })
          .catch(() => {});
      }
    });

    socket.on('groups:updated', loadStatus);

    socket.on('buzz:taken', ({ groupName, groupColor, remainingMs }: { groupName: string; groupColor: string; remainingMs: number }) => {
      setBuzzer({ taken: true, takenBy: groupName, takenByColor: groupColor });
      clearTimer();
      setTimeLeftMs(remainingMs);
      audioRef.current?.pause();
      if (stopTimeoutRef.current) { clearTimeout(stopTimeoutRef.current); stopTimeoutRef.current = null; }
    });

    socket.on('buzz:wrong', ({ remainingMs }: { remainingMs: number }) => {
      setBuzzer({ taken: false, takenBy: null, takenByColor: null });
      startTimer(remainingMs);
      const audio = audioRef.current;
      if (audio && audio.src && !audio.ended) {
        audio.play().catch(() => {});
        if (audio.currentTime < (audio.duration || Infinity)) {
          const segRemaining = (audio.duration - audio.currentTime) * 1000;
          if (segRemaining > 0) {
            stopTimeoutRef.current = setTimeout(() => audio.pause(), Math.min(segRemaining, remainingMs));
          }
        }
      }
    });
    socket.on('buzz:reset', () => setBuzzer({ taken: false, takenBy: null, takenByColor: null }));

    socket.on('answers:all_received', () => setAllAnswered(true));

    socket.on('intru:vote_received', (v: IntruVoteReceived) => {
      setIntruVotes((prev) => [...prev.filter((x) => x.groupId !== v.groupId), v]);
    });

    socket.on('intru:all_voted', () => setAllVoted(true));

    socket.on('serie:intru_vote', ({ serieId, musics }: any) => {
      setActiveIntruSerieId(serieId);
      setActiveIntruMusics(musics ?? []);
      setIntruVotes([]);
      setAllVoted(false);
      setIntruReveal(null);
      setSerieEnded(false);
    });

    socket.on('serie:intru_reveal', (data: IntruRevealPayload) => {
      setIntruReveal(data);
      setScores(data.scores);
      setActiveIntruSerieId(null);
      setActiveIntruMusics([]);
    });

    return () => {
      ['game:started', 'game:ended', 'game:serie_started', 'game:serie_ended',
       'question:start', 'question:end', 'question:reveal',
       'scores:update', 'answer:received', 'groups:updated', 'buzz:taken', 'buzz:wrong',
       'buzz:reset', 'answers:all_received', 'intru:vote_received', 'intru:all_voted',
       'serie:intru_vote', 'serie:intru_reveal', 'admin:question:detail',
       'admin:intru:detail'].forEach((e) => socket.off(e));
      clearTimer();
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
    };
  }, [loadStatus, loadSeries, startTimer]);

  // Deferred audio play after React re-renders
  useEffect(() => {
    if (pendingPlayRef.current && audioRef.current) {
      playAudio(pendingPlayRef.current);
      pendingPlayRef.current = null;
    }
  }, [status?.currentQuestion?.id, playAudio]);

  const doAction = async (label: string, fn: () => Promise<any>) => {
    setLoading(label); setError('');
    try { await fn(); await loadStatus(); } catch (e: any) { setError(e.message); }
    setLoading(null);
  };

  const openRegistrations = () => doAction('open', () => api.post('/api/game/open-registrations', {}));
  const startGame = () => doAction('start', () => api.post('/api/game/start', {}));
  const startSerie = () => {
    if (!selectedSerieId) return;
    doAction('serie', () => api.post('/api/game/serie/start', { serieId: parseInt(selectedSerieId) }));
  };
  const nextQuestion = () => doAction('next', () => api.post('/api/game/next-question'));
  const endCurrentQuestion = () => doAction('endQ', () => api.post('/api/game/end-question'));
  const endGame = () => { setConfirmEndGame(false); doAction('end', () => api.post('/api/game/end')); };

  const markAnswer = async (a: ReceivedAnswer, correct: boolean) => {
    try {
      await api.post(`/api/game/question/${a.questionId}/mark`, { groupId: a.groupId, correct });
      setAnswers((prev) => prev.map((x) => x.groupId === a.groupId ? { ...x, marked: true, correct } : x));
    } catch (e: any) { setError(e.message); }
  };

  const handleReveal = () => {
    getSocket().emit('admin:reveal', {}, () => {});
  };

  const validateBuzzer = (correct: boolean) => {
    getSocket().emit('admin:buzz:validate', { correct });
    setBuzzer({ taken: false, takenBy: null, takenByColor: null });
  };

  const playSegment = () => {
    const q = status?.currentQuestion;
    if (q) playAudio(q);
  };

  const revealIntru = () => {
    if (!activeIntruSerieId) return;
    getSocket().emit('admin:intru:reveal', { serieId: activeIntruSerieId }, (res: any) => {
      if (!res?.success) setError(res?.error || 'Impossible de révéler l\'intru');
    });
  };

  const game = status?.game;
  const q = status?.currentQuestion;
  const isPlaying = game?.status === 'in_progress';
  const hasSerie = !!game?.currentSerieId;
  const isUntimed = q?.durationMs === 0;
  const pct = q && timeLeftMs > 0 ? (timeLeftMs / q.durationMs) * 100 : 0;
  const timerColor = pct > 50 ? '#22c55e' : pct > 25 ? '#f59e0b' : '#ef4444';
  const answeredCount = answers.length;
  const connectedCount = status?.groups.filter((g) => g.connected).length ?? 0;

  // Which series haven't been started yet? (or show all if we can't track)
  const availableSeries = series;

  return (
    <div className="dashboard">
      {error && <div className="alert alert-danger">{error}</div>}

      {/* Game controls */}
      <div className="dashboard-controls card">
        <div className="status-row">
          <span className={`status-dot ${game?.status}`} />
          <span className="status-label">
            {game?.status === 'waiting' ? 'En attente'
              : game?.status === 'in_progress' ? (status?.currentSerie ? `Série : ${status.currentSerie.name}` : 'Partie en cours')
              : 'Terminée'}
          </span>
          {q && hasSerie && (
            <span className="question-counter">
              Q{(game?.currentQuestionIndex ?? 0) + 1}
            </span>
          )}
          {isPlaying && connectedCount > 0 && q && (
            <span className="answer-progress" title="Réponses reçues">
              {answeredCount}/{connectedCount} réponses
              {allAnswered && <span className="all-answered-badge"> ✓ Tous</span>}
            </span>
          )}
        </div>

        {/* Series picker: shown when game is running but no active serie (start or between series) */}
        {isPlaying && !hasSerie && !activeIntruSerieId && availableSeries.length > 0 && (
          <div className="serie-picker-row">
            <label>Choisir la série :</label>
            <select value={selectedSerieId} onChange={(e) => setSelectedSerieId(e.target.value)}>
              <option value="">— Sélectionnez une série —</option>
              {availableSeries.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <button
              className="btn btn-primary"
              onClick={startSerie}
              disabled={!selectedSerieId || !!loading}
            >
              {loading === 'serie' ? '...' : '▶ Démarrer cette série'}
            </button>
          </div>
        )}

        <div className="btn-row">
          {game?.status === 'ended' && (
            <button className="btn btn-primary" onClick={openRegistrations} disabled={!!loading}>
              {loading === 'open' ? '...' : '🚪 Ouvrir les inscriptions'}
            </button>
          )}
          {(!game || game.status === 'waiting') && (
            <button className="btn btn-success" onClick={startGame} disabled={!!loading}>
              {loading === 'start' ? '...' : '▶ Démarrer la partie'}
            </button>
          )}
          {isPlaying && (
            <>
              {/* Reveal button */}
              {q && !revealData && (questionEnded || allAnswered) && !activeIntruSerieId && (
                <button className="btn btn-reveal" onClick={handleReveal}>
                  🎯 Révéler les résultats
                </button>
              )}
              {/* End current (untimed) question */}
              {q && isUntimed && !questionEnded && (
                <button className="btn btn-warning" onClick={endCurrentQuestion} disabled={!!loading}>
                  {loading === 'endQ' ? '...' : '⏹ Stopper les réponses'}
                </button>
              )}
              {/* Next question — disabled when intru is active */}
              {hasSerie && !activeIntruSerieId && (
                <button
                  className="btn btn-primary"
                  onClick={nextQuestion}
                  disabled={!!loading}
                >
                  {loading === 'next' ? '...' : 'Question suivante →'}
                </button>
              )}
              {confirmEndGame ? (
                <div className="confirm-end-row">
                  <span className="confirm-end-label">Terminer la partie ?</span>
                  <button className="btn btn-danger btn-sm" onClick={endGame} disabled={!!loading}>
                    Oui, terminer
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setConfirmEndGame(false)}>
                    Annuler
                  </button>
                </div>
              ) : (
                <button className="btn btn-danger" onClick={() => setConfirmEndGame(true)} disabled={!!loading}>
                  ⏹ Terminer
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Active intru vote panel (auto-started, no button needed) */}
      {activeIntruSerieId && (
        <div className="card intru-vote-panel">
          <div className="intru-vote-panel-header">
            <h3 className="card-title">🕵 Vote intru — {intruDetail?.serieName ?? `Série #${activeIntruSerieId}`}</h3>
            <span className="text-muted">
              {intruVotes.length}/{connectedCount} votes
              {allVoted && <span className="badge badge-success" style={{ marginLeft: 8 }}>Tous ont voté ✓</span>}
            </span>
          </div>
          {intruDetail && (
            <div className="intru-answer-reveal">
              <span className="label-sm">L'intru est :</span>
              <strong className="intru-song-name">{intruDetail.intruTitle}</strong>
              {intruDetail.intruArtist && <span className="text-muted"> — {intruDetail.intruArtist}</span>}
              {intruDetail.intruJustification && (
                <blockquote className="intru-justif-admin" style={{ marginTop: 6 }}>{intruDetail.intruJustification}</blockquote>
              )}
              <div className="intru-musics-list" style={{ marginTop: 8 }}>
                <span className="label-sm">Chansons de la série :</span>
                {intruDetail.musics.map((m, i) => (
                  <span key={m.id} className={`intru-music-tag ${m.id === intruDetail.intruQuestionId ? 'intru-music-answer' : ''}`}>
                    {String.fromCharCode(65 + i)}. {m.songTitle || m.title}{m.artist ? ` — ${m.artist}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}
          <table className="answers-table" style={{ marginTop: 10 }}>
            <thead>
              <tr><th>Équipe</th><th>Réponse</th><th>Justification</th></tr>
            </thead>
            <tbody>
              {intruVotes.length === 0 ? (
                <tr><td colSpan={3} className="text-muted" style={{ padding: '10px 0' }}>En attente des votes...</td></tr>
              ) : intruVotes.map((v) => {
                const music = activeIntruMusics.find((m: any) => m.id === v.chosenQuestionId);
                const label = music ? (music.songTitle || music.title) : `Q#${v.chosenQuestionId}`;
                const isCorrect = intruDetail && v.chosenQuestionId === intruDetail.intruQuestionId;
                return (
                  <tr key={v.groupId} className={isCorrect ? 'row-correct' : 'row-wrong'}>
                    <td><span className="team-dot" style={{ background: v.groupColor }} />{v.groupName}</td>
                    <td>{label}</td>
                    <td className="justification-cell">{v.justification || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button
            className="btn btn-reveal"
            onClick={revealIntru}
            style={{ marginTop: 12 }}
          >
            🎯 Révéler l'intru
          </button>
        </div>
      )}

      {/* Intru reveal result */}
      {intruReveal && !activeIntruSerieId && (
        <div className="card intru-reveal-panel">
          <h3 className="card-title">🕵 Résultats du vote intru</h3>
          <div className="intru-answer">
            <strong>L'intru était : </strong>
            {intruReveal.intruTitle}{intruReveal.intruArtist ? ` — ${intruReveal.intruArtist}` : ''}
          </div>
          {intruReveal.intruJustification && (
            <blockquote className="intru-justif-admin">{intruReveal.intruJustification}</blockquote>
          )}
          <table className="answers-table">
            <thead>
              <tr><th>Équipe</th><th>Réponse</th><th>Justification</th><th>Résultat</th></tr>
            </thead>
            <tbody>
              {intruReveal.results.map((r) => (
                <tr key={r.groupId} className={r.correct ? 'row-correct' : 'row-wrong'}>
                  <td>
                    <span className="team-dot" style={{ background: r.groupColor }} />
                    {r.groupName}
                  </td>
                  <td>{r.correct ? '✅' : '❌'} {r.correct
                    ? (intruReveal.intruTitle || `Q#${r.chosenQuestionId}`)
                    : `Q#${r.chosenQuestionId}`}
                  </td>
                  <td className="justification-cell">{r.justification || '—'}</td>
                  <td>{r.correct ? `+${r.points}` : '0 pt'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="dashboard-grid">
        <div className="dashboard-left">
          {/* Current question */}
          {q && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Question courante</h3>
                {isUntimed && <span className="badge badge-info">Sans minuterie</span>}
              </div>
              <p className="question-title">{q.title}</p>
              {(q.songTitle || q.artist) && (
                <p className="question-song text-muted">
                  {q.songTitle}{q.songTitle && q.artist ? ' — ' : ''}{q.artist}
                </p>
              )}
              {correctAnswerHint && (
                <p className="correct-answer-hint">
                  Réponse attendue : <strong>{correctAnswerHint}</strong>
                </p>
              )}
              <div className="meta-row">
                <span className="badge">{q.answerType}</span>
                <span className="badge">{q.maxPoints} pts</span>
                {q.dynamicScoring && <span className="badge badge-accent">Dynamique</span>}
              </div>

              {/* Timer (timed questions only) */}
              {!isUntimed && timeLeftMs > 0 && (
                <div className="timer-wrap">
                  <div className="timer-bar-wrap">
                    <div className="timer-bar" style={{ width: `${pct}%`, background: timerColor }} />
                  </div>
                  <span style={{ color: timerColor, fontWeight: 700 }}>{Math.ceil(timeLeftMs / 1000)}s</span>
                </div>
              )}

              {questionEnded && !revealData && !isUntimed && (
                <div className="question-ended-banner">
                  ⏱ Temps écoulé — Validez les réponses puis cliquez "Révéler"
                </div>
              )}

              {/* Audio player */}
              {q.audioFile && (
                <div className="audio-section">
                  <audio ref={audioRef} controls style={{ width: '100%' }} />
                  <div className="audio-meta">
                    <span>Segment : {(q.startMs / 1000).toFixed(1)}s → {(q.endMs / 1000).toFixed(1)}s</span>
                    <button className="btn btn-sm btn-secondary" onClick={playSegment}>▶ Rejouer</button>
                  </div>
                </div>
              )}

              {/* Buzzer */}
              {q.answerType === 'buzzer' && (
                <div className={`buzzer-panel ${buzzer.taken ? 'buzzer-taken' : ''}`}>
                  {buzzer.taken ? (
                    <>
                      <div className="buzz-indicator" style={{ borderColor: buzzer.takenByColor || '#7c3aed' }}>
                        <span style={{ color: buzzer.takenByColor || '#7c3aed' }}>🔔 {buzzer.takenBy}</span> a le buzzer !
                      </div>
                      <div className="btn-row">
                        <button className="btn btn-success" onClick={() => validateBuzzer(true)}>✓ Bonne réponse</button>
                        <button className="btn btn-danger" onClick={() => validateBuzzer(false)}>✗ Mauvaise réponse</button>
                      </div>
                    </>
                  ) : (
                    <p className="text-muted">En attente d'un buzz...</p>
                  )}
                </div>
              )}

              {/* Reveal result */}
              {revealData && (
                <div className="reveal-panel">
                  <strong>✓ Résultats révélés</strong>
                  {revealData.correctAnswer && (
                    <span> — Bonne réponse : <em>{revealData.correctAnswer}</em></span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Answers table */}
          {q && q.answerType !== 'buzzer' && (
            <div className="card">
              <h3 className="card-title">
                Réponses reçues ({answeredCount}/{connectedCount})
                {allAnswered && <span className="badge badge-success ml-2">Tous ✓</span>}
              </h3>
              {answers.length === 0 ? (
                <p className="text-muted">Aucune réponse</p>
              ) : (
                <table className="answers-table">
                  <thead>
                    <tr>
                      <th>Équipe</th>
                      <th>Réponse</th>
                      <th>{q.answerType === 'text' ? 'Valider' : 'Résultat'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answers.map((a) => {
                      const reveal = revealData?.results.find((r) => r.groupId === a.groupId);
                      return (
                        <tr
                          key={a.groupId}
                          className={reveal ? (reveal.correct ? 'row-correct' : 'row-wrong') : a.marked ? (a.correct ? 'row-correct' : 'row-wrong') : ''}
                        >
                          <td>
                            <span className="team-dot" style={{ background: a.groupColor }} />
                            {a.groupName}
                          </td>
                          <td>{a.value}</td>
                          <td>
                            {reveal ? (
                              <span>{reveal.correct ? '✅' : '❌'} {reveal.points > 0 ? `+${reveal.points}` : ''}</span>
                            ) : q.answerType === 'text' ? (
                              <div className="btn-row-sm" style={{ alignItems: 'center' }}>
                                {a.marked && (
                                  <span className="mark-current" title="Marquage actuel">
                                    {a.correct ? '✅' : '❌'}
                                  </span>
                                )}
                                <button
                                  className={`btn btn-sm ${a.correct === true ? 'btn-success' : 'btn-secondary'}`}
                                  onClick={() => markAnswer(a, true)}
                                  title="Bonne réponse"
                                >
                                  ✓
                                </button>
                                <button
                                  className={`btn btn-sm ${a.correct === false ? 'btn-danger' : 'btn-secondary'}`}
                                  onClick={() => markAnswer(a, false)}
                                  title="Mauvaise réponse"
                                >
                                  ✗
                                </button>
                              </div>
                            ) : (
                              <span>{a.correct === true ? '✅' : a.correct === false ? '❌' : '⏳'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {q.answerType === 'text' && !revealData && (questionEnded || allAnswered) && (
                <div className="reveal-row">
                  <button className="btn btn-reveal" onClick={handleReveal}>
                    🎯 Révéler les résultats aux joueurs
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right panel */}
        <div className="dashboard-right">
          <div className="card">
            <h3 className="card-title">Scores en direct</h3>
            <ScoreBoard scores={scores} />
          </div>
          <div className="card">
            <h3 className="card-title">Équipes connectées</h3>
            {!status?.groups.length && <p className="text-muted">Aucune équipe</p>}
            <div className="groups-list">
              {status?.groups.map((g) => (
                <div key={g.id} className={`group-chip ${g.connected ? 'connected' : 'disconnected'}`}>
                  <span className="group-dot" style={{ background: g.color }} />
                  <span>{g.name}</span>
                  <span className="group-pts">{g.totalPoints} pts</span>
                  {!g.connected && <span className="disconnected-label">hors ligne</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
